








































































































































import { Component, Vue, Watch } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import type { Trade } from '@/models/trade';
import type { Phase } from '@/models/phase';
import type { Lesson } from '@/models/lesson';
import type { User } from '@/models/user';
import type { CompanySetting } from '@/models/companySetting';
import type { Topic } from '@/models/topic';
import type { Tag } from '@/models/tag';

import { phaseWebservice } from '@/webservices/phase';
import { tradeWebservice } from '@/webservices/trade';
import { topicWebservice } from '@/webservices/topic';
import { tagWebservice } from '@/webservices/tag';

import { ApprovalState } from '@/components/prine/enums/ApprovalState';
import SearchField from '@/components/prine/SearchField.vue';
import StoreUpdateLessonModal from '@/components/knowledge/lessons/StoreUpdateLessonModal.vue';
import LessonsList from '@/components/knowledge/lessons/LessonsList.vue';
import FilteringSetting from '@/components/knowledge/lessons/FilteringSetting.vue';

import { FilterManager } from '@/helpers/filter/FilterManager';
import { FilterOptions } from '@/helpers/filter/models/FilterOptions';
import { FilterData } from '@/helpers/filter/models/FilterData';
import MoreInfoModal from '@/components/MoreInfoModal.vue';
import { hasPermission } from '@/helpers/auth/permissionHelpers';
import { PERMISSIONS } from '@/helpers/auth/permissions';

@Component({
    name: 'GroupedLessonsList',
    computed: {
        PERMISSIONS() {
            return PERMISSIONS;
        },
    },
    methods: { hasPermission },
    components: {
        StoreUpdateLessonModal,
        SearchField,
        FilteringSetting,
        MoreInfoModal,
        LessonsList,
    },
})
export default class GroupedLessonsList extends Vue {
    private moreInfoShowModalActive: boolean = false;
    private moreInfoModalInsight: string;
    private moreInfoModalContent: string;
    private moreInfoModalId: number;
    private tableCollapsed: boolean = false;

    private option: {
        filterByColumn: boolean;
        filterable: boolean;
    };

    private phases = new Array<Phase>();
    private tags = new Array<Tag>();
    private filteredTopics = new Array<Topic>();
    private originalTopics = new Array<Topic>();
    private tradeId: number;
    private selectedTrade: Trade = {} as Trade;
    private selectedPhase: Phase = {} as Phase;
    private allPhases: Phase = {} as Phase;
    private selectedTag = {} as Tag;
    private allTags: Tag = {} as Tag;
    private selectedTopic: Topic = {} as Topic;
    private selectedLesson: Lesson = {} as Lesson;

    private isStoreUpdateLessonModalActive = false;
    private isFilteringSettingActive = false;

    private showOnlyMyLessons = false;
    private isSearching: boolean = false;
    private searchQuery: string = '';

    private filterManager: FilterManager = new FilterManager();
    private filterOptions: FilterOptions = {} as FilterOptions;
    private openedDetailRows: number[] = [];
    private isReady: boolean = false;

    @Getter('account/userInfo') private userInfo: User;
    @Getter('account/companySetting') private companySetting: CompanySetting;
    @Getter('utils/currentLocale') private currentLocale: string;

    @Watch('searchQuery')
    private onSearchQueryValueChange() {
        this.search();
    }

    @Watch('showEmptyTopics')
    private onFiltersChanged() {
        this.filter();
    }

    private mounted() {
        // The id is passed as string, parse it
        if (this.$route.query.tradeId) {
            this.tradeId = Number(this.$route.query.tradeId);
        }
        this.loadTrade();
        this.loadPhases();
        this.loadTags();
        this.isReady = true;
    }

    private init() {
        this.openedDetailRows = this.filteredTopics.map((el) => el.id);
    }

    get showEmptyTopics() {
        return this.$store.getters['knowledge/showEmptyTopics'];
    }

    set showEmptyTopics(value: boolean) {
        this.$store.commit('knowledge/setShowEmptyTopics', value);
    }

    private updateSearchQuery(value: string) {
        this.searchQuery = value;
    }

    private showOnlyMyLessonsCopy(value: boolean) {
        this.showOnlyMyLessons = value;
    }

    private search() {
        this.isSearching = true;
        this.filter();
        this.isSearching = false;
    }

    private filter() {
        const data = JSON.parse(JSON.stringify(this.originalTopics)) as FilterData[];
        this.setFilterOptions();
        this.filteredTopics = (this.filterManager.filter(data, this.filterOptions) as unknown) as Topic[];
    }

    private initializeMoreInfoModal(lesson: Lesson) {
        this.moreInfoModalContent = lesson.more_info;
        this.moreInfoModalInsight = lesson.insight;
        this.moreInfoModalId = lesson.id;
        this.moreInfoShowModalActive = true;
    }

    private setFilterOptions() {
        this.filterOptions.userId = this.userInfo.id;
        this.filterOptions.searchQuery = this.searchQuery;
        this.filterOptions.tag = this.selectedTag;
        this.filterOptions.phase = this.selectedPhase;
        this.filterOptions.showEmptyElements = this.showEmptyTopics;
        this.filterOptions.onlyMyLessons = this.showOnlyMyLessons;
    }

    private goBack() {
        this.$router.push({
            name: 'CombinedKnowledge',
            query: {
                searchQuery: encodeURIComponent(this.searchQuery),
                filterByLessons: this.$route.query.filterByLessons,
            },
        });
    }

    private loadTrade() {
        tradeWebservice.getById(this.tradeId.toString()).then((response) => {
            this.selectedTrade = response.data;
        });
    }

    private loadPhases() {
        phaseWebservice.load().then((response) => {
            this.phases = response.data;

            this.allPhases.id = 0;
            this.allPhases.abbreviation = this.$t('keywords.all').toString();
            this.allPhases.title = this.$t('keywords.allPhases').toString();

            // getting selectedPhaseId from query
            const selectedPhaseId = Number(this.$route.query.selectedPhaseId);
            // finding the phase with selectedPhaseId
            const phase = this.phases.find((el) => el.id === selectedPhaseId);

            if (phase) {
                this.selectedPhase = phase;
            } else {
                this.selectedPhase = this.allPhases;
            }

            // Add it in the front of the array
            this.phases.unshift(this.allPhases);

            // Load all lessons when the trade is loaded
            this.loadLessons();
        });
    }

    private loadTags() {
        tagWebservice
            .load()
            .then((response) => {
                this.tags = response.data;

                this.allTags.id = 0;
                this.allTags.title = this.$t('keywords.allTags').toString();

                this.selectedTag = this.allTags;

                // Add it in the front of the array
                this.tags.unshift(this.allTags);

                // getting selectedTagId from the query and select the matching tag
                const selectedTagId = Number(this.$route.query.selectedTagId);
                const tag = this.tags.find((el) => el.id === selectedTagId);

                if (tag) {
                    this.selectedTag = tag;
                    this.isFilteringSettingActive = true;
                }

                if (this.originalTopics && this.originalTopics.length > 0) {
                    this.filter();
                }
            })
            .catch((error) => {
                console.error(error);
            });
    }

    private loadLessons() {
        // get List With Questions By tradeId With Lessons and phases
        topicWebservice
            .loadTopicsByTradeAndPhaseWithLessons(this.tradeId, this.allPhases.id, ApprovalState.ALL)
            .then((response) => {
                this.originalTopics = response.data;
                if (this.$route.query.searchQuery && this.$route.query.searchQuery !== '') {
                    this.searchQuery = decodeURIComponent(this.$route.query.searchQuery.toString());
                }
                this.filter();
                this.init();
            })
            .catch((error) => {
                console.error(error);
            });
    }

    private hr(index: number) {
        return index > 1;
    }

    private reload() {
        this.loadLessons();
    }

    private toggleStoreUpdateLessonModal(lesson: Lesson = {} as Lesson) {
        this.selectedLesson = lesson;
        this.isStoreUpdateLessonModalActive = !this.isStoreUpdateLessonModalActive;

        // Only load the lessons when the modal is not active
        if (!this.isStoreUpdateLessonModalActive) {
            this.loadLessons();
        }
    }

    private toggleFilteringSettings() {
        this.isFilteringSettingActive = !this.isFilteringSettingActive;
    }

    private phaseChanged(value: Phase) {
        this.selectedPhase = value;
        this.filter();
    }

    private tagChanged(value: Tag) {
        this.selectedTag = value;
        this.filter();
    }

    private showOnlyMyLessonsChanged(value: boolean) {
        this.showOnlyMyLessons = value;
        this.filter();
    }

    private clearSearch() {
        // Reset the search query
        this.searchQuery = '';
    }

    get canResetFilter() {
        return (
            this.selectedTag !== this.allTags ||
            this.selectedPhase !== this.allPhases ||
            this.showEmptyTopics ||
            this.showOnlyMyLessons
        );
    }

    private resetFilter() {
        this.selectedTag = this.allTags;
        this.selectedPhase = this.allPhases;
        this.showEmptyTopics = false;
        this.showOnlyMyLessons = false;

        this.filter();
    }

    private toggleDetailRows() {
        if (this.tableCollapsed) {
            this.openedDetailRows = this.filteredTopics.map((el) => el.id);
        } else {
            this.openedDetailRows = [];
        }
        this.tableCollapsed = !this.tableCollapsed;
    }

    private getLessonsCount(lessons: Lesson[]) {
        const count = (cLessons: Lesson[]): number => {
            return cLessons.reduce((base, lesson) => {
                if (lesson.sub_lessons) {
                    base += count(lesson.sub_lessons);
                }

                return base + 1;
            }, 0);
        };

        return count(lessons);
    }
}
